import { useNavigate } from 'react-router-dom'
import { faBellExclamation } from '@fortawesome/pro-solid-svg-icons'
import { Alert, VisualTimeline } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectFollowupAlertData,
  selectDisplayPhaseTimelineSteps,
  selectTopFollowupItem,
  selectBlockingItemsAlertData,
  selectHasActionRequired,
} from './cubk.selectors'
import { useHasEffectiveActionRequired } from './hooks/useHasEffectiveActionRequired'
import { CubkProgressPanel } from './CubkProgressPanel'
import { BlockingItemsAlert } from './BlockingItemsAlert'
import { CUBK_FOLLOWUP_ALERT_DISMISSED_KEY } from './cubkCard.constants'

export const CubkCardExpanded = () => {
  const navigate = useNavigate()
  const { hasEffectiveActionRequired: hasActionRequired } =
    useHasEffectiveActionRequired()
  const hasFollowupActionRequired = useReselector(selectHasActionRequired)
  const followupAlert = useReselector(selectFollowupAlertData)
  const timelineSteps = useReselector(selectDisplayPhaseTimelineSteps)
  const topFollowupItem = useReselector(selectTopFollowupItem)
  const blockingItems = useReselector(selectBlockingItemsAlertData)

  const handleFollowupItemAction = () => {
    if (topFollowupItem?.route) {
      navigate(topFollowupItem.route, { state: { fromGettingStarted: true } })
    }
  }

  const visualTimelineSteps = timelineSteps.map((step) => ({
    label: step.label,
    status: step.status,
    icon: step.icon,
    iconClassName: step.iconClassName,
    badge: step.badge,
  }))

  return (
    <div className="mt-8" data-testid="cubk-card-expanded">
      {/* Action required banners. Followup takes priority — blocking items banner
          is suppressed when the followup banner is active. Wrapped in a flex
          column with gap to handle spacing consistently for either state. */}
      <div className="flex flex-col gap-4">
        {/* Contextual alert for bookkeeper-initiated followup actions */}
        {hasActionRequired && followupAlert && topFollowupItem && (
          <Alert
            variant="priority"
            customIconName={faBellExclamation}
            title={`Action required: ${followupAlert.title}`}
            description={followupAlert.description}
            dismissible
            permanentDismissalKey={CUBK_FOLLOWUP_ALERT_DISMISSED_KEY}
            ctaText="Start"
            ctaOnClick={handleFollowupItemAction}
            ctaProps={{ variant: 'primary' }}
            data-testid="cubk-followup-alert"
          />
        )}

        {/* Blocking items banner: session-only dismissal so it re-appears if items recur.
            Suppressed when the followup alert is active (followup takes priority). */}
        {blockingItems.length > 0 &&
          !(hasFollowupActionRequired && followupAlert && topFollowupItem) && (
            <BlockingItemsAlert items={blockingItems} />
          )}
      </div>

      <div className="flex flex-col lg:flex-row items-start gap-8 mt-8">
        <div className="w-full lg:w-1/2">
          <VisualTimeline steps={visualTimelineSteps} />
        </div>
        <div className="w-full lg:w-1/2">
          <CubkProgressPanel />
        </div>
      </div>
    </div>
  )
}
